import React, { createContext, useState, useContext, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { Model, ModelRegistry } from '../types/model';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext'; // Import useToast
import { useLocalStorage } from '../hooks/useLocalStorage';



/** Lookup only — no synthetic tier objects. */
export function getTierModel(id: string, registry: ModelRegistry | null | undefined): Model | null {
  if (!registry) return null;
  return registry[id] ?? null;
}

/**
 * Placeholder for `currentModel` only while signed-in and initial-payload is still loading.
 * All real tier metadata must come from `modelRegistry`.
 */
export const AUTO_LOADING_MODEL: Model = {
  id: 'auto',
  displayName: 'Auto',
  isReasoning: false,
  canSeeImages: true,
  canUseTools: true,
  lab: 'system',
};

function areModelsEquivalent(a: Model, b: Model): boolean {
  return (
    a.id === b.id &&
    a.displayName === b.displayName &&
    a.subtext === b.subtext &&
    a.icon === b.icon &&
    a.isReasoning === b.isReasoning &&
    a.canSeeImages === b.canSeeImages &&
    a.canUseTools === b.canUseTools &&
    a.isUnavailable === b.isUnavailable &&
    a.lab === b.lab &&
    a.speed === b.speed &&
    a.starRating === b.starRating &&
    a.additional === b.additional
  );
}

export interface ModelContextType {
  currentModel: Model;
  setCurrentModel: (model: Model) => void;
  isModelModalOpen: boolean;
  setIsModelModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  checkModelCompatibility: (isImageFileAttached: boolean, isWebSearchEnabled?: boolean, messagesHaveImages?: boolean) => boolean;
  currentModelSetter: ((model: Model) => void);
  setCurrentModelSetter: React.Dispatch<React.SetStateAction<(model: Model) => void>>;
}

interface ModelContextProviderProps {
  children: ReactNode;
}

const ModelContext = createContext<ModelContextType | undefined>(undefined);

export const ModelContextProvider: React.FC<ModelContextProviderProps> = ({ children }) => {
  const { modelRegistry, initialPayloadLoaded, user } = useAuth();
  const toast = useToast();

  const [currentModel, rawSetCurrentModel] = useLocalStorage<Model>(
    'currentModel',
    AUTO_LOADING_MODEL,
    { enabled: !!user }
  );
  const [isModelModalOpen, setIsModelModalOpen] = useState<boolean>(false);
  const [currentModelSetter, setCurrentModelSetter] = useState<((model: Model) => void)>(() => rawSetCurrentModel);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem('hotkeyModels');
    window.localStorage.removeItem('displayedModels');
    window.localStorage.removeItem('showMessagesModel');
  }, []);

  const attemptSetCurrentModel = useCallback((modelToSet: Model) => {
    rawSetCurrentModel(modelToSet);
  }, [rawSetCurrentModel]);

  // Keep `currentModel` aligned with server registry entries (fixes stale localStorage + loads tier metadata).
  useEffect(() => {
    if (!user || !initialPayloadLoaded) return;
    if (!modelRegistry || Object.keys(modelRegistry).length === 0) return;

    const resolved = modelRegistry[currentModel.id];
    if (resolved) {
      if (!areModelsEquivalent(resolved, currentModel)) {
        attemptSetCurrentModel(resolved);
      }
      return;
    }

    attemptSetCurrentModel(modelRegistry.auto);
  }, [currentModel, modelRegistry, attemptSetCurrentModel, user, initialPayloadLoaded]);

  const checkModelCompatibility = useCallback((isImageFileAttached: boolean, isWebSearchEnabled: boolean = false, messagesHaveImages: boolean = false): boolean => {
    if ((isImageFileAttached || messagesHaveImages) && !currentModel.canSeeImages) {
      toast("This model does not support images", "error");
      return false;
    }
    
    if (isWebSearchEnabled && !currentModel.canUseTools) {
      toast("Web search requires a model that supports tools. Please select a different model or disable web search.", "error");
      return false;
    }
    
    return true;
  }, [currentModel, toast]);

  const contextValue = useMemo(() => ({
    currentModel,
    setCurrentModel: attemptSetCurrentModel,
    isModelModalOpen,
    setIsModelModalOpen,
    checkModelCompatibility,
    currentModelSetter,
    setCurrentModelSetter
  }), [
    currentModel, attemptSetCurrentModel, isModelModalOpen, setIsModelModalOpen,
    checkModelCompatibility,
    currentModelSetter,
    setCurrentModelSetter
  ]);

  return (
    <ModelContext.Provider value={contextValue}>
      {children}
    </ModelContext.Provider>
  );
};

export const useModelContext = (): ModelContextType => {
  const context = useContext(ModelContext);
  if (!context) {
    throw new Error("useModelContext must be used within a ModelContextProvider");
  }
  return context;
};
